/**
 * Toggle Visibility Action
 * Triggered by: arc:action:toggleVisibility
 *
 * This action allows you to show, hide, or toggle the visibility of elements
 * matching specific CSS selectors with various methods and animations.
 */

export const runVisibilityToggle = async (data = {}) => {
  try {
    const {
      selectors = [],
      eventId,
      targetEventId,
      action = 'toggle', // 'show', 'hide', 'toggle'
      method = 'display', // 'display', 'visibility', 'opacity', 'class'
      transition = '0.3s ease',
      className = 'hidden',
      fadeAnimation = false,
      slideAnimation = false,
      callback = null,
      important = false
    } = data;

    // Build selectors array with unique event IDs if provided
    let finalSelectors = [];
    if (selectors.length > 0) {
      finalSelectors = Array.isArray(selectors) ? selectors : [selectors];
    } else if (targetEventId) {
      finalSelectors = [`#${targetEventId}`];
    } else if (eventId) {
      finalSelectors = [`#${eventId}`];
    } else {
      finalSelectors = ['body']; // fallback
    }

    // Validate action parameter
    const validActions = ['show', 'hide', 'toggle'];
    if (!validActions.includes(action)) {
      throw new Error(`Invalid action. Must be one of: ${validActions.join(', ')}`);
    }

    // Validate method parameter
    const validMethods = ['display', 'visibility', 'opacity', 'class'];
    if (!validMethods.includes(method)) {
      throw new Error(`Invalid method. Must be one of: ${validMethods.join(', ')}`);
    }

    if (finalSelectors.length === 0) {
      throw new Error('At least one CSS selector must be provided');
    }

    const changedElements = [];
    const failedSelectors = [];

    // Helper function to determine if element is currently visible
    const isElementVisible = (element) => {
      const computedStyle = window.getComputedStyle(element);

      switch (method) {
        case 'display':
          return computedStyle.display !== 'none';
        case 'visibility':
          return computedStyle.visibility !== 'hidden';
        case 'opacity':
          return parseFloat(computedStyle.opacity) > 0;
        case 'class':
          return !element.classList.contains(className);
        default:
          return true;
      }
    };

    // Helper function to show element
    const showElement = (element) => {
      console.log(`Showing element:`, element);

      if (transition && (method === 'opacity' || fadeAnimation)) {
        element.style.transition = `opacity ${transition}`;
      }

      if (slideAnimation) {
        element.style.transition = `height ${transition}, opacity ${transition}`;
      }

      switch (method) {
        case 'display':
          const originalDisplay = element.dataset.originalDisplay || 'block';
          element.style.setProperty('display', originalDisplay, important ? 'important' : '');
          break;
        case 'visibility':
          element.style.setProperty('visibility', 'visible', important ? 'important' : '');
          break;
        case 'opacity':
          element.style.setProperty('opacity', '1', important ? 'important' : '');
          break;
        case 'class':
          element.classList.remove(className);
          break;
      }

      // Handle slide animation
      if (slideAnimation && method === 'display') {
        element.style.height = '0px';
        element.style.overflow = 'hidden';
        element.style.opacity = '0';

        requestAnimationFrame(() => {
          element.style.height = element.scrollHeight + 'px';
          element.style.opacity = '1';

          setTimeout(() => {
            element.style.height = '';
            element.style.overflow = '';
          }, parseFloat(transition) * 1000);
        });
      }
    };

    // Helper function to hide element
    const hideElement = (element) => {
      console.log(`Hiding element:`, element);

      if (transition && (method === 'opacity' || fadeAnimation)) {
        element.style.transition = `opacity ${transition}`;
      }

      if (slideAnimation) {
        element.style.transition = `height ${transition}, opacity ${transition}`;
      }

      // Store original display value for later restoration
      if (method === 'display' && !element.dataset.originalDisplay) {
        const computedStyle = window.getComputedStyle(element);
        element.dataset.originalDisplay = computedStyle.display !== 'none' ? computedStyle.display : 'block';
      }

      // Handle slide animation
      if (slideAnimation && method === 'display') {
        element.style.height = element.scrollHeight + 'px';
        element.style.overflow = 'hidden';

        requestAnimationFrame(() => {
          element.style.height = '0px';
          element.style.opacity = '0';

          setTimeout(() => {
            element.style.setProperty('display', 'none', important ? 'important' : '');
          }, parseFloat(transition) * 1000);
        });
        return;
      }

      switch (method) {
        case 'display':
          element.style.setProperty('display', 'none', important ? 'important' : '');
          break;
        case 'visibility':
          element.style.setProperty('visibility', 'hidden', important ? 'important' : '');
          break;
        case 'opacity':
          element.style.setProperty('opacity', '0', important ? 'important' : '');
          break;
        case 'class':
          element.classList.add(className);
          break;
      }
    };

    // Process each selector
    for (const selector of finalSelectors) {
      try {
        const elements = document.querySelectorAll(selector);

        console.log(`Searching for selector: "${selector}" - Found ${elements.length} elements`);

        if (elements.length === 0) {
          failedSelectors.push({
            selector,
            reason: 'No elements found'
          });
          continue;
        }

        elements.forEach((element, index) => {
          console.log(`Processing element ${index + 1} for selector "${selector}":`, element);

          const wasVisible = isElementVisible(element);
          console.log(`Element was visible: ${wasVisible}`);

          let newVisibility;

          // Determine what action to take
          if (action === 'show') {
            showElement(element);
            newVisibility = true;
          } else if (action === 'hide') {
            hideElement(element);
            newVisibility = false;
          } else {
            // toggle
            if (wasVisible) {
              hideElement(element);
              newVisibility = false;
            } else {
              showElement(element);
              newVisibility = true;
            }
          }

          // Force a reflow to ensure the change is applied
          element.offsetHeight;

          changedElements.push({
            element,
            selector,
            action: action === 'toggle' ? (newVisibility ? 'showed' : 'hid') : action,
            elementTag: element.tagName,
            elementClasses: element.className,
            elementId: element.id,
            wasVisible,
            isVisible: newVisibility,
            method
          });
        });
      } catch (error) {
        console.error(`Error processing selector "${selector}":`, error);
        failedSelectors.push({
          selector,
          reason: error.message
        });
      }
    }

    // Execute callback if provided
    if (callback && typeof callback === 'function') {
      try {
        callback(changedElements);
      } catch (callbackError) {
        console.warn('Callback execution failed:', callbackError);
      }
    }

    const result = {
      success: true,
      message: `Visibility ${action} applied to ${changedElements.length} elements`,
      data: {
        action,
        method,
        changedElements: changedElements.length,
        successfulSelectors: finalSelectors.filter((s) => !failedSelectors.find((f) => f.selector === s)),
        failedSelectors,
        transition,
        changedElementsDetails: changedElements.map((el) => ({
          selector: el.selector,
          elementTag: el.elementTag,
          elementClasses: el.elementClasses,
          elementId: el.elementId,
          action: el.action,
          wasVisible: el.wasVisible,
          isVisible: el.isVisible
        }))
      }
    };

    // Also log a simple verification
    if (changedElements.length === 0) {
      console.warn('⚠️ No elements were changed! Check if your selectors match existing elements.');
      console.log('Available elements to debug:', {
        allDivs: document.querySelectorAll('div').length,
        allSections: document.querySelectorAll('section').length,
        elementsWithHiddenClass: document.querySelectorAll('.hidden').length,
        elementsWithShowClass: document.querySelectorAll('.show').length
      });
    }

    return result;
  } catch (error) {
    console.error('Toggle Visibility Error:', error);
    return {
      success: false,
      error: error.message,
      data: {
        providedData: data
      }
    };
  }
};

export default runVisibilityToggle;

// Helper function for testing and debugging
export const testVisibilityToggle = () => {
  console.log('Testing Visibility Toggle...');

  // Test with body element (always exists)
  window.dispatchEvent(
    new CustomEvent('arc:action:toggleVisibility', {
      detail: {
        selectors: ['body'],
        action: 'toggle',
        method: 'opacity',
        transition: '0.5s ease'
      }
    })
  );

  // Also try direct function call for immediate testing
  runToggleVisibility({
    selectors: ['div'],
    action: 'hide',
    method: 'display',
    transition: '0.3s ease'
  }).then((result) => {
    console.log('Direct function call result:', result);
  });
};
